import React from 'react'
import _ from 'lodash/fp'
import classnames from 'classnames'

import withStyles from '@material-ui/core/styles/withStyles'
import colors from '@fe/ui/colors'
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@material-ui/core'

import { getDiffColor } from './Charts/util'

const stylesHoleRow = {
  selected: {
    backgroundColor: colors.secondaryLightGrey
  },
  selectedHoleNumber: {
    backgroundColor: colors.primaryLightGreen,
    color: colors.white
  },
  holeNumber: {
    fontSize: 'x-large',
    fontWeight: 100
  },
  total: {
    color: 'rgba(0, 0, 0, 0.54)'
  },
  clickable: {
    cursor: 'pointer'
  }
}

const strokeParser = (ch) => {
  if (!ch) {
    return ''
  }
  let decor = ''
  if (ch < 0) {
    decor = '-'
  }
  return `${decor}${ch >= 5 ? ch : _.repeat(ch, '|')}`
}

const wrapperStyle = (diff) => ({
  border: 'solid transparent 1px',
  display: 'flex',
  width: 30 - (Math.abs(diff) * 4),
  height: 30 - (Math.abs(diff) * 4),
  justifyContent: 'center',
  alignItems: 'center',
  margin: 'auto'
})

const addBorder = (component, diff, borderColor) => {
  if (component === 0) { // If no shots: Do nothing
    return '0'
  }
  if (diff === 0) { // diff === 0 <==> (par || addedBorders); Do nothing
    return <div style={{ ...wrapperStyle(diff) }}>{component}</div>
  } else if (diff < 0) { // Negative diff means below par, so add round border
    return addBorder(<div style={{ ...wrapperStyle(diff), borderRadius: '50%', borderColor }}>{component}</div>, diff + 1, borderColor)
  }
  // otherwise diff must be positve which means above par, so add square border
  return addBorder(<div style={{ ...wrapperStyle(diff), borderColor }}>{component}</div>, diff - 1, borderColor)
}

const renderShotsCell = ({ points, nShots, par, handicapComparison }) => {
  const diff = handicapComparison ? 2 - points : nShots - par
  return addBorder(nShots, _.clamp(-2, 2, diff), getDiffColor(diff))
}

const GenericRow = withStyles(stylesHoleRow)(({
  holes,
  classes,
  selected,
  onClick,
  getter,
  name,
  handicapComparison
}) => {
  const holeNumbers = name === ''
  const sum = (entries) => {
    // Summing of holeNumbers should show in/out/total instead of sums
    if (holeNumbers) {
      // 1+2+3+4+5+6+7+8+9 = 45
      if (_.sum(entries) === 45) {
        return 'out'
        // 10+11+12+13+14+15+16+17+18 = 126
      } else if (_.sum(entries) === 126) {
        return 'in'
      }
      return 'total'
    } else if (name === 'CH') {
      return _.flow(
        _.sum,
        _.size,
        strokeParser
      )(entries)
    }
    return _.sum(entries)
  }
  const numHoles = _.size(holes)
  return (
    <TableRow>
      <TableCell className={classnames(classes.total)} align='left'>
        {name}
      </TableCell>
      {_.map.convert({ cap: false })((hole, index) => {
        const key = _.get('holeId', hole)
        const value = name === 'Shots' ? renderShotsCell({ ...hole, handicapComparison }) : getter(hole)
        const item = (
          <TableCell
            align='center'
            key={key}
            className={classnames(
              selected(hole) && holeNumbers && classes.selectedHoleNumber,
              selected(hole) && classes.selected,
              holeNumbers && classes.holeNumber,
              onClick && classes.clickable
            )}
            onClick={onClick ? onClick(hole) : undefined}
          >
            {value}
          </TableCell>
        )
        if (index === 8 && numHoles === 18) {
          return (
            <React.Fragment key={`${key}-out`}>
              {item}
              <TableCell className={classnames(classes.total)} align='center'>
                {_.flow(
                  _.dropRight(numHoles - 9),
                  _.map(getter),
                  sum
                )(holes)}
              </TableCell>
            </React.Fragment>
          )
        }
        if (index === 17 && numHoles === 18) {
          return (
            <React.Fragment key={`${key}-in`}>
              {item}
              <TableCell className={classnames(classes.total)} align='right'>
                {_.flow(
                  _.dropRight(numHoles - 18),
                  _.drop(9),
                  _.map(getter),
                  sum
                )(holes)}
              </TableCell>
            </React.Fragment>
          )
        }
        return item
      }, holes)}
      <TableCell className={classnames(classes.total)} align='right'>
        {_.flow(
          _.map(getter),
          sum
        )(holes)}
      </TableCell>
    </TableRow>
  )
})

const Scorecard = ({ holes, selectedHole, setSelectedHole, handicapComparison }) => {
  if (!holes) {
    return null
  }

  const isSelected = (hole) => selectedHole === _.get('holeId', hole)
  const onHoleClick = setSelectedHole && (
    (hole) => () => setSelectedHole(_.get('holeId', hole))
  )

  return (
    <Table style={{ marginBottom: 12 }}>
      <TableHead>
        <GenericRow
          holes={holes}
          selected={isSelected}
          onClick={onHoleClick}
          getter={_.get('holeOrder')}
          name=''
        />
      </TableHead>
      <TableBody>
        <GenericRow
          holes={holes}
          selected={isSelected}
          onClick={onHoleClick}
          getter={_.get('distance')}
          name='Dist'
        />
        <GenericRow
          holes={holes}
          selected={isSelected}
          onClick={onHoleClick}
          getter={_.get('par')}
          name='Par'
        />
        <GenericRow
          holes={holes}
          selected={isSelected}
          onClick={onHoleClick}
          getter={_.get('handicap')}
          name='HCP'
        />
        <GenericRow
          holes={holes}
          selected={isSelected}
          onClick={onHoleClick}
          getter={(hole) => strokeParser(hole.ch)}
          name='CH'
        />
        <GenericRow
          holes={holes}
          selected={isSelected}
          onClick={onHoleClick}
          getter={_.get('nShots')}
          name='Shots'
          handicapComparison={handicapComparison}
        />
        <GenericRow
          holes={holes}
          selected={isSelected}
          onClick={onHoleClick}
          getter={_.get('points')}
          name='Points'
        />
      </TableBody>
    </Table>
  )
}

export default Scorecard
